/**
 * Il NEGOZIO degli avatar, solo il riscatto gratuito (I-612): la parte pura, senza React e senza rete.
 *
 * Le risposte di Qavatar (`/avatars/shop/*`) si leggono QUI, in modo difensivo: una voce che non si legge si scarta (mai
 * un'eccezione che butta giu' la schermata), e una busta che non e' `{ enabled, … }` e' un errore di CONTRATTO (un array nudo
 * non e' mai la forma giusta: lo diciamo, non lo indoviniamo).
 */

/** Una voce del negozio come la mostra la schermata. `libero` = `price === null` (gratuito); ogni altro valore NON e' libero. */
export interface VoceNegozio {
  itemId: string;
  snapshotId: string;
  title: string;
  description: string;
  /** Le URL del server, mai HTML. `thumbnail` ha la precedenza sulla `image` per l'anteprima. */
  image?: string;
  thumbnail?: string;
  libero: boolean;
  /** `null` = «non so dirlo» (lo specchio non garantisce): mai trattato come `false`. */
  owned: boolean | null;
}

export interface NegozioPagina {
  enabled: boolean;
  items: VoceNegozio[];
  nextCursor: string | null;
}

export type StatoSpecchio = 'ok' | 'realigning' | 'degraded';

export interface NegozioPosseduti {
  enabled: boolean;
  /** `false` = «non so dirlo»: va detto, mai mostrato come «nessun oggetto». Solo `true` esatto vale verificato. */
  verified: boolean;
  mirror: StatoSpecchio | null;
  items: VoceNegozio[];
  nextCursor: string | null;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const testo = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

export class ErroreContratto extends Error {
  constructor(dove: string) {
    super(`avatar shop: la risposta di ${dove} non ha la forma attesa`);
    this.name = 'ErroreContratto';
  }
}

/** Una voce di `/items` o `/owned`; `null` se non si legge (manca l'id o il titolo). */
export function leggiVoce(raw: unknown): VoceNegozio | null {
  if (!isObj(raw)) return null;
  const itemId = testo(raw.itemId);
  const title = testo(raw.title);
  if (!itemId || !title) return null;
  const media = isObj(raw.media) ? raw.media : {};
  return {
    itemId,
    snapshotId: testo(raw.snapshotId) ?? '',
    title,
    description: typeof raw.description === 'string' ? raw.description : '',
    image: testo(media.image),
    thumbnail: testo(media.thumbnail),
    // Solo `null` esatto e' gratuito: un prezzo, ma anche un campo assente o illeggibile, NON mostra l'articolo fra i gratuiti.
    libero: raw.price === null,
    owned: raw.owned === true ? true : raw.owned === false ? false : null,
  };
}

function leggiVoci(raw: unknown): VoceNegozio[] {
  if (!Array.isArray(raw)) return [];
  const voci: VoceNegozio[] = [];
  for (const r of raw) {
    const v = leggiVoce(r);
    if (v) voci.push(v);
  }
  return voci;
}

/** `GET /avatars/shop/items`. Negozio spento = `{ enabled:false, items:[] }`, NON un errore. */
export function leggiPagina(raw: unknown): NegozioPagina {
  if (!isObj(raw) || typeof raw.enabled !== 'boolean') throw new ErroreContratto('/avatars/shop/items');
  return { enabled: raw.enabled, items: leggiVoci(raw.items), nextCursor: testo(raw.nextCursor) ?? null };
}

/** `GET /avatars/shop/owned`. */
export function leggiPosseduti(raw: unknown): NegozioPosseduti {
  if (!isObj(raw) || typeof raw.enabled !== 'boolean') throw new ErroreContratto('/avatars/shop/owned');
  const m = raw.mirror;
  return {
    enabled: raw.enabled,
    verified: raw.verified === true,
    mirror: m === 'ok' || m === 'realigning' || m === 'degraded' ? m : null,
    items: leggiVoci(raw.items),
    nextCursor: testo(raw.nextCursor) ?? null,
  };
}

/**
 * Questo articolo e' quello in vetrina? `data` e' la risposta di `GET /avatars/featured` (o della PATCH feature): per un articolo
 * porta l'`item.itemId` (o l'id dell'istantanea). Si leggono entrambi, in modo difensivo: un dato che non si legge = «non in
 * vetrina», mai un'eccezione.
 */
export function inVetrina(featured: unknown, voce: Pick<VoceNegozio, 'itemId' | 'snapshotId'>): boolean {
  if (!isObj(featured)) return false;
  const item = isObj(featured.item) ? featured.item : undefined;
  if (item && item.itemId === voce.itemId) return true;
  if (featured.itemId === voce.itemId) return true;
  return voce.snapshotId !== '' && (featured._id === voce.snapshotId || featured.avatarId === voce.snapshotId);
}

/** Solo i gratuiti: gli articoli con `price === null`. Un articolo con un prezzo (o con un prezzo che non si legge) non si mostra mai. */
export function soloGratuiti(voci: readonly VoceNegozio[]): VoceNegozio[] {
  return voci.filter((v) => v.libero);
}

/**
 * Il codice di un rifiuto del negozio, o `undefined`. ⚠️ I campi sono DUE, secondo la rotta (QAvatar `feat/QAvatar`, letto il 11/10):
 *  · `PATCH …/feature` manda `{ success:false, code, message }` (routes/shopFeature.ts);
 *  · `POST …/redeem`, `GET …/items` e `GET …/owned` mandano `{ error }` (services/shopRedeem.ts, routes/shopReads.ts).
 * Chi chiama dice quale campo legge: leggere quello sbagliato non da' un errore, da' «nessun codice» e la frase generica.
 */
export function codiceNegozio(errore: unknown, campo: 'error' | 'code'): string | undefined {
  const c = (errore as { data?: Record<string, unknown> } | null | undefined)?.data?.[campo];
  return typeof c === 'string' ? c : undefined;
}

/** I secondi di un `Retry-After` (solo la forma «secondi interi», 1..3600); assente o illeggibile = `undefined` («fra poco»). */
export function secondiDa(valore: string | null | undefined): number | undefined {
  if (!valore || !/^\d{1,4}$/.test(valore.trim())) return undefined;
  const n = Number(valore.trim());
  return n >= 1 && n <= 3600 ? n : undefined;
}
